/**
 * File: FeedbackForm.tsx
 * Description: Form component for feedback input
 * Author: System
 * Created: 2025-10-06
 * Last Modified: 2025-10-06
 */

import React from 'react';

import { cn } from '../../../shared/utils';

import type { FeedbackFormProps } from './types';

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  comment,
  setComment,
  includeContext,
  setIncludeContext,
  includeUserName,
  setIncludeUserName,
  isSubmitting,
  onSubmit,
  onCancel,
  feedbackType,
}) => {
  return (
    <form onSubmit={onSubmit}>
      {/* Comment */}
      <div className="mb-4">
        <label
          htmlFor="feedback-comment"
          className="block text-xs font-medium text-gray-900 mb-2"
        >
          Comment (optional)
        </label>
        <textarea
          id="feedback-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={isSubmitting}
          className="w-full p-3 border border-gray-300 rounded bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y text-xs min-h-[60px] disabled:bg-gray-50 disabled:cursor-not-allowed"
          rows={3}
          placeholder="Share your thoughts..."
        />
      </div>

      {/* Checkboxes */}
      <div className="space-y-3 mb-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeContext}
            onChange={(e) => setIncludeContext(e.target.checked)}
            disabled={isSubmitting}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:cursor-not-allowed"
          />
          <span className="text-xs text-gray-900">
            Include conversation context
          </span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeUserName}
            onChange={(e) => setIncludeUserName(e.target.checked)}
            disabled={isSubmitting}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:cursor-not-allowed"
          />
          <span className="text-xs text-gray-900">Include my username</span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className={cn(
            'px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex items-center gap-2',
            feedbackType === 'up'
              ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
              : 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          )}
        >
          {isSubmitting && (
            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </form>
  );
};
